import React, { useState, useRef, useEffect } from 'react';
import FAQSection from './FAQ';
import { Link, NavLink } from 'react-router-dom';
import { navItems } from '../utils/nav-config'; // ✅ ensure path is correct
import '../utils/footer.css';
import GlyphSvg from '../components/Glyphs';

export default function Footer() {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [version, setVersion] = useState('');
  const timeoutRef = useRef(null);

  // Load version from revision.json
  useEffect(() => {
    fetch('/revision.json')
      .then(response => response.json())
      .then(data => {
        setVersion(data.version);
      })
      .catch(() => {
        // Fallback to package.json version
        setVersion(import.meta.env.VITE_VERSION || '0.1.0');
      });
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleSocialClick = (e) => {
    e.preventDefault();
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });

    // If tooltip is already showing, start fade out
    if (showTooltip) {
      setIsFadingOut(true);
      timeoutRef.current = setTimeout(() => {
        setShowTooltip(false);
        setIsFadingOut(false);
      }, 300); // Match the fade-out animation duration
    } else {
      // Show new tooltip
      setShowTooltip(true);
      setIsFadingOut(false);
      
      // Set timeout to fade out after 3 seconds
      timeoutRef.current = setTimeout(() => {
        setIsFadingOut(true);
        setTimeout(() => {
          setShowTooltip(false);
          setIsFadingOut(false);
        }, 300); // Match the fade-out animation duration
      }, 3000);
    }
  };

  return (
    <>
      <FAQSection />

      <footer
        className="b-c5 c-c0 py-5 footer-nav"
        style={{
          padding: '1rem',
          marginTop: '2rem',
          backgroundRepeat: 'repeat',
        }}
      >
        {/* Tooltip */}
        {showTooltip && (
          <div 
            className={`tooltip ${isFadingOut ? 'fade-out' : ''}`}
            style={{
              left: tooltipPosition.x,
              top: tooltipPosition.y
            }}
          >
            Coming soon
          </div>
        )}
        <div className="container">
          <div className="row text-start">
            {/* LEFT SIDE: SOCIAL + ITORA (stacked on desktop, side-by-side on mobile) */}
            <div className="col-12 col-md-8">
              <div className="row">
                <div className="col-6 col-md-6 mb-4">
                  <ul className="list-unstyled">
                    <li className="f-a f-uc mb-3 fs-4">Social</li>
                    <li className="f-b">
                      <GlyphSvg glyph="g06" size="8" color="var(--c0)" /> &nbsp;
                      <a href="/" className="link-secondary c-c0 social-link" onClick={handleSocialClick}>
                        Instagram
                      </a>
                    </li>
                    <li className="f-b">
                      <GlyphSvg glyph="g06" size="8" color="var(--c0)" /> &nbsp;
                      <a href="/" className="link-secondary c-c0 social-link" onClick={handleSocialClick}>
                        YouTube
                      </a>
                    </li>
                    <li className="f-b">
                      <GlyphSvg glyph="g06" size="8" color="var(--c0)" /> &nbsp;
                      <a href="/" className="link-secondary c-c0 social-link" onClick={handleSocialClick}>
                        TikTok
                      </a>
                    </li>
                    <li className="f-b">
                      <GlyphSvg glyph="g06" size="8" color="var(--c0)" /> &nbsp;
                      <a href="/" className="link-secondary c-c0 social-link" onClick={handleSocialClick}>
                        LinkedIn
                      </a>
                    </li>
                  </ul>
                </div>

                <div className="col-6 col-md-6 mb-4">
                  <ul className="list-unstyled">
                    <li className="f-a f-uc mb-3 fs-3">ITORA</li>
                    {navItems.map(({ path, label }) => (
                      <li key={path} className="f-b">
                        <GlyphSvg glyph="g06" size="8" color="var(--c0)" /> &nbsp;
                        <NavLink to={path} className="link-secondary c-c0">
                          {label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* RIGHT SIDE: HAVE QUESTIONS */}
            <div className="col-12 col-md-4 mb-4">
              <div>Have questions?</div>
              <Link
                to="/contact"
                className="btn btn-link py-2 px-3 mb-3 b-c5 o-c0 c-c0"
              >
                Contact us
              </Link>
              <div>Get updates</div>
              <div className="c-c1 mb-2">
                Drop us your email to stay in touch.
              </div>
              <form action="/" className="input-group mb-3">
                <input
                  type="email"
                  className="form-control"
                  placeholder="Email"
                  required
                  aria-label="Your email"
                />
                <button
                  className="btn btn-outline-secondary b-c0 c-c00"
                  type="submit"
                >
                  <GlyphSvg glyph="g06" size="12" color="var(--c5)" />
                </button>
              </form>
            </div>
          </div>

          <hr className='c-c4' />

          <div className="row align-items-center text-uppercase small">
            <div className="col-md-9 col-sm-6 c-c4 text-md-start text-center mb-2 mb-md-0">
              <div>©{new Date().getFullYear()} ITORA.</div>
              <div className="text-lowercase" style={{fontSize: '0.7rem', opacity: 0.6}}>
                iteration r{version}
              </div>
            </div>
            <div className="col-md-3 col-sm-6 text-md-end text-center">
              <a href="/privacy-policy" className="link-secondary me-3 c-c4">
                Privacy policy
              </a>
              <a href="/terms-of-service" className="link-secondary c-c4">
                Terms of service
              </a>
            </div>
          </div>
        </div>
      </footer>


    </>
  );
}
